/**
 * Migration-Rollback (Admin) — Phase 6.
 *
 * Juni 2026 — Notfall-/Support-Rückabwicklung eines Migrationslaufs:
 *   1. User per E-Mail suchen → user_id.
 *   2. Dry-Run: Vorschau, welche Datensätze gelöscht würden.
 *   3. Bestätigter Rollback: löscht committed Kunden/Income/Expenses (per
 *      `migration_id`) + Staging + Migration, schreibt Audit-Log.
 *
 * Endpoints:
 *   GET  /api/admin/users?search=
 *   POST /api/admin/migration/:user_id/rollback?dry_run=true|false
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Search, RotateCcw, AlertTriangle, Loader2, Trash2, LifeBuoy, Zap, Paperclip, Download, Sparkles } from 'lucide-react';

const COUNT_LABELS = {
  customers: 'Kunden', income: 'Ausgangsrechnungen', expenses: 'Ausgaben',
  standard_articles: 'Standardartikel',
  migration_items: 'Staging-Einträge', migration_files: 'Hochgeladene Dateien',
};

const fmtDate = (iso) => {
  if (!iso) return '–';
  try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function MigrationRollbackAdminPanel() {
  const [email, setEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [user, setUser] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [preview, setPreview] = useState(null);   // dry-run result
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rolling, setRolling] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [linkedRequest, setLinkedRequest] = useState(null);   // Support-Anfrage, aus der der Rollback ausgelöst wurde

  const reset = () => { setUser(null); setPreview(null); setNotFound(false); setLinkedRequest(null); };

  const downloadSharedFile = async (requestId, idx, name) => {
    try {
      const res = await api.get(`/admin/support-requests/${requestId}/files/${idx}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = name || 'import-datei';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.status === 410 ? 'Datei nicht mehr verfügbar (ggf. bereits gelöscht).' : 'Download fehlgeschlagen');
    }
  };

  const loadRequests = useCallback(async () => {
    setLoadingReqs(true);
    try {
      const res = await api.get('/admin/migration/rollback-requests');
      setRequests(res.data.items || []);
    } catch {
      /* still usable ohne Liste */
    } finally { setLoadingReqs(false); }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const searchUser = async () => {
    if (!email.trim()) return;
    setSearching(true);
    reset();
    try {
      const res = await api.get(`/admin/users?search=${encodeURIComponent(email.trim())}&limit=10`);
      const exact = (res.data.users || []).find(
        (u) => (u.email || '').toLowerCase() === email.trim().toLowerCase());
      const picked = exact || (res.data.users || [])[0];
      if (!picked) { setNotFound(true); return; }
      setUser(picked);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suche fehlgeschlagen');
    } finally { setSearching(false); }
  };

  const loadPreview = async (uid) => {
    const targetId = uid || user?.id;
    if (!targetId) return;
    setLoadingPreview(true);
    setPreview(null);
    try {
      const res = await api.post(`/admin/migration/${targetId}/rollback?dry_run=true`);
      setPreview(res.data.would_delete);
    } catch (e) {
      if (e.response?.status === 404) toast.error('Keine Migration für diesen User vorhanden');
      else toast.error(e.response?.data?.detail || 'Vorschau fehlgeschlagen');
    } finally { setLoadingPreview(false); }
  };

  // 1-Klick aus einer Support-Anfrage: User setzen + Vorschau laden
  const prepareFromRequest = async (req) => {
    setEmail(req.user_email || '');
    setNotFound(false);
    setUser({ id: req.user_id, email: req.user_email, name: req.user_name, plan: req.user_plan || '—' });
    setLinkedRequest(req);
    await loadPreview(req.user_id);
  };

  const doRollback = async () => {
    if (!user) return;
    setRolling(true);
    try {
      const res = await api.post(`/admin/migration/${user.id}/rollback`);
      const d = res.data.deleted || {};
      const total = Object.values(d).reduce((a, b) => a + b, 0);
      toast.success(`Rollback abgeschlossen — ${total} Datensätze entfernt`);
      // Verknüpfte Support-Anfrage automatisch auf „Erledigt" setzen + Nutzer informieren
      if (linkedRequest?.id) {
        try {
          await api.patch(`/admin/support-requests/${linkedRequest.id}`, { status: 'done', notify_user: true });
          toast.success(`Ticket ${linkedRequest.ticket_number || ''} erledigt · Nutzer informiert`);
        } catch { /* nicht-blockierend */ }
        loadRequests();
      }
      setConfirmOpen(false);
      setPreview(null);
      setUser(null);
      setEmail('');
      setLinkedRequest(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rollback fehlgeschlagen');
    } finally { setRolling(false); }
  };

  const totalToDelete = preview ? Object.values(preview).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-5" data-testid="migration-rollback-panel">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <RotateCcw className="h-6 w-6 text-rose-600" /> Migration-Rollback
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Macht einen Migrationslauf vollständig rückgängig (Support-/Notfall-Werkzeug).
          Es werden nur Datensätze gelöscht, die über den Umzugs-Assistenten angelegt wurden.
        </p>
      </div>

      {/* Warn-Banner */}
      <div className="rounded-xl border-2 border-rose-200 bg-rose-50 p-4 flex gap-3" data-testid="rollback-warning">
        <AlertTriangle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-rose-800">
          <strong>Unwiderruflich.</strong> Die gelöschten Live-Datensätze (Kunden, Rechnungen, Ausgaben)
          können nicht wiederhergestellt werden. Prüfe immer zuerst die Vorschau und stelle sicher, dass es
          die richtige Person ist. Jede Aktion wird im Audit-Log protokolliert.
        </div>
      </div>

      {/* Offene Rollback-Supportanfragen — 1-Klick-Bearbeitung */}
      <Card data-testid="rollback-requests-card">
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <LifeBuoy className="h-4 w-4 text-emerald-600" /> Offene Rollback-Anfragen
              <span className="text-xs font-normal text-slate-400">({requests.length})</span>
            </Label>
            <Button variant="ghost" size="sm" onClick={loadRequests} disabled={loadingReqs} data-testid="rollback-requests-refresh">
              {loadingReqs ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aktualisieren'}
            </Button>
          </div>

          {requests.length === 0 ? (
            <p className="text-sm text-slate-400 py-2" data-testid="rollback-requests-empty">
              Keine offenen Migrations-Rollback-Anfragen.
            </p>
          ) : (
            <div className="space-y-2">
              {requests.map((r) => (
                <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3 flex items-start justify-between gap-3" data-testid={`rollback-request-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.ticket_number && (
                        <span className="px-2 py-0.5 rounded-md text-xs font-mono font-semibold bg-slate-100 text-slate-700 border border-slate-200">{r.ticket_number}</span>
                      )}
                      <span className="font-medium text-slate-800 text-sm truncate">{r.user_email}</span>
                      <span className="text-xs text-slate-400">· {fmtDate(r.created_at)}</span>
                      {r.migration_status && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Migration: {r.migration_status}</span>
                      )}
                      {r.ai_active === true && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 inline-flex items-center gap-1" data-testid={`rollback-ai-active-${r.id}`}>
                          <Sparkles className="h-3 w-3" /> KI aktiv
                        </span>
                      )}
                      {r.ai_active === false && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200" data-testid={`rollback-ai-inactive-${r.id}`}>
                          Keine KI
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap">{r.message}</p>
                    {r.shared_files && r.shared_files.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`rollback-request-files-${r.id}`}>
                        <span className="text-[11px] font-medium text-slate-500 flex items-center gap-1">
                          <Paperclip className="h-3 w-3" /> Beigefügte Dateien:
                        </span>
                        {r.shared_files.map((f) => (
                          <button
                            key={f.index}
                            onClick={() => downloadSharedFile(r.id, f.index, f.name)}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-emerald-50 hover:border-emerald-200 transition-colors"
                            title={`${f.name} (${Math.max(1, Math.round((f.size_bytes || 0) / 1024))} KB)`}
                            data-testid={`rollback-file-download-${r.id}-${f.index}`}
                          >
                            <Download className="h-3 w-3" /> {f.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => prepareFromRequest(r)}
                    disabled={loadingPreview || !r.migration_status}
                    title={!r.migration_status ? 'Keine Migration vorhanden' : 'Vorschau laden & Rollback vorbereiten'}
                    className="shrink-0 bg-rose-600 hover:bg-rose-700"
                    data-testid={`rollback-request-prepare-${r.id}`}
                  >
                    <Zap className="h-4 w-4 mr-1.5" /> Rollback vorbereiten
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User-Suche */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <Label className="text-xs">User per E-Mail suchen</Label>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="kunde@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUser()}
              data-testid="rollback-email-input"
            />
            <Button onClick={searchUser} disabled={searching} data-testid="rollback-search-btn">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-1.5">Suchen</span>
            </Button>
          </div>

          {notFound && (
            <p className="text-sm text-rose-600" data-testid="rollback-not-found">Kein User mit dieser E-Mail gefunden.</p>
          )}

          {user && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between" data-testid="rollback-user-card">
              <div>
                <div className="font-medium text-slate-800">{user.name || '–'}</div>
                <div className="text-xs text-slate-500">{user.email} · Plan: {user.plan} · ID: <span className="font-mono">{user.id}</span></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => loadPreview()} disabled={loadingPreview} data-testid="rollback-preview-btn">
                {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Search className="h-4 w-4 mr-1.5" />}
                Vorschau laden
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dry-Run-Vorschau */}
      {preview && (
        <Card data-testid="rollback-preview">
          <CardContent className="py-4 space-y-4">
            <div className="text-sm font-medium text-slate-700">
              Diese Datensätze würden gelöscht ({totalToDelete} gesamt):
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(COUNT_LABELS).map(([k, lbl]) => (
                <div key={k} className="rounded-lg bg-slate-50 p-3 text-center" data-testid={`rollback-count-${k}`}>
                  <div className={`text-2xl font-bold ${preview[k] > 0 ? 'text-rose-600' : 'text-slate-300'}`}>{preview[k] ?? 0}</div>
                  <div className="text-xs text-slate-500">{lbl}</div>
                </div>
              ))}
            </div>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={totalToDelete === 0}
              data-testid="rollback-execute-btn"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Rollback jetzt ausführen
            </Button>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="rollback-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Migration wirklich zurücksetzen?</AlertDialogTitle>
            <AlertDialogDescription>
              Für <strong>{user?.email}</strong> werden {totalToDelete} Datensätze unwiderruflich gelöscht
              und die Migration zurückgesetzt. Diese Aktion wird im Audit-Log festgehalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={doRollback} disabled={rolling} className="bg-rose-600 hover:bg-rose-700" data-testid="rollback-confirm-action">
              {rolling ? 'Setzt zurück…' : 'Ja, Rollback ausführen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
